import React, {useContext, useEffect, useRef, useState} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import Row from "@amzn/meridian/row";
import Loader from "../../loader/Loader";
import { LEDControllerConfigurationContext } from "./context/LEDControllerConfigurationContext";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {PermissionsContext} from "../../permissions/PermissionsContext";
import {CONFIG_TYPE, DEFAULT_AISLE_TYPE, OPERATION_COLOR} from "../../util/Constants";
import {chainWalk, getAssetRefreshInterval} from "../../util/Util";
import Column from "@amzn/meridian/column";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import PaginatedTable from "../../commonui/PaginatedTable";
import {ConditionalWrapping, tooltipWrapper} from "../../util/ConditionalWrapping";
import {getLocalizedMessage} from "../../util/Localization";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import updateTokens from "@amzn/meridian-tokens/base/icon/edit";
import Box from "@amzn/meridian/box";
import {TableCell, TableRow} from "@amzn/meridian/table";
import {
    getMaxPortsForLedControllerType,
    hasLedPortAboveLimit,
    validateUniqueEntries,
    validateUpdateLedController
} from "../../util/Validations";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import AisleRowModal from "./modal/AisleRowModal";
import Alert from "@amzn/meridian/alert";
import {
    createNextAisleConfig,
    getLastAisleConfig, createFirstAisleConfig, getSortedData
} from "../../util/LEDControllerUtil";
import AddMultipleAisleRowsModal from "./modal/AddMultipleAisleRowsModal";

const LEDControllerConfiguration = ({intl}) => {
    const {viewModel, dispatch} = useContext(LEDControllerConfigurationContext);
    const {getDraftData, setDraftData} = useContext(DraftContext);
    const {isAuthorized} = useContext(PermissionsContext);
    const [valueConfiguration, setValueConfiguration] = useState(CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION)
    const [openAddRowsModal, setOpenAddRowsModal] = useState(false);
    const [selectedMac, setSelectedMac] = useState("")
    const [clipboard, setClipBoard] = useState(null)
    const [tableData, setTableData] = useState([])
    const tableEndRef = useRef(null)
    const {showCustomAlert} = useContext(GlobalStateContext);
    const {showWarning} = useContext(GlobalStateContext);

    // Visual controls
    const [showMissingAlert, setShowMissingAlert] = useState(false)
    const [showAisleDuplicateAlert, setShowAisleDuplicateAlert] = useState(false)
    const [showPortDuplicateAlert, setShowPortDuplicateAlert] = useState(false)
    const [showPortLimitAlert, setShowPortLimitAlert] = useState(false)

    const maxPorts = getMaxPortsForLedControllerType(viewModel.ledControllerType)

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION]

    useEffect(() => {
        getAisleTypes();
    }, []);

    useEffect(() => {
        if (!openAddRowsModal) {
            loadDevices()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    loadDevices();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddRowsModal]);

    const getAisleTypes = () => {
        if (viewModel.aisleTypes.size === 0) {
            dispatch({type: 'LOAD_AISLE_TYPES', data: {dispatcher: dispatch}})
        }
    }

    const loadDevices = () => {
        setOpenAddRowsModal(false)
        dispatch({type: 'LOAD_DEVICES', data: {dispatcher: dispatch}});
    }

    useEffect(() => {
        if (selectedMac) {
            setDraftData({
                data: [],
                stateKey: CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION,
                subKey: selectedMac,
                override: true
            })
            dispatch({type: 'LOAD_ASSET_DETAIL', data: {dispatcher: dispatch, mac: selectedMac}});
        } else {
            viewModel.ledAisleData = []
        }
    }, [selectedMac]);

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => viewModel.ledAisleData, []),
            stateKey: CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
    }, [viewModel.ledAisleData]);

    const getLedDraft = () => {
        return getDraftData({stateKey: CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION, subKey: selectedMac})
    }

    useEffect(() => {
        const d = getLedDraft()
        setTableData(d)
    }, [getLedDraft()]);

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const getSortedDevices = () => {
        const updatedDevices = Object.values(viewModel.ledDevices).map(device => ({
            ...device,
            name: device.location
        }));
        return getSortedData(updatedDevices)
    }

    const headerProps = {
        selectedMac,
        setSelectedMac,
        valueConfiguration,
        setValueConfiguration,
        isSorterConfig: valueConfiguration === CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION,
        intl,
        scrollToBottom,
        defaultSelection: CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION,
        sortedDeviceList: getSortedDevices(),
        configurationKeys: PAGE_CONFIGURATION_KEYS,
        refreshDeps: [viewModel.timestamp],
        shouldNotRenderConfigSelection: true,
        isViewOnly: true
    }

    const renderTableRow = (config) => {
        return (
            <TableRow
                highlightOnHover={true}
                key={config.index}
                backgroundColor={OPERATION_COLOR[config.operation]}
                >
                <AisleRowModal
                    config={config}
                    mac={selectedMac}
                    getDraftData={getLedDraft}
                    setDraftData={setDraftData}
                    aisleTypes={[...viewModel.aisleTypes, DEFAULT_AISLE_TYPE] || []}
                    maxPorts={maxPorts}
                    clipBoard={clipboard}
                    setClipBoard={setClipBoard}
                />
            </TableRow>
        )
    }

    const renderTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell sortColumn="name">
                    <FormattedMessage id="modal.aislename" defaultMessage="Aisle Name"/>
                </TableCell>
                <TableCell sortColumn="port">
                    <FormattedMessage id="modal.port" defaultMessage="Port"/>
                </TableCell>
                <TableCell sortColumn="aisleType">
                    <FormattedMessage id="modal.aisletype" defaultMessage="Aisle Type"/>
                </TableCell>
                <TableCell sortColumn="actions">
                    <FormattedMessage id="modal.actions" defaultMessage="Actions"/>
                </TableCell>
            </TableRow>
        )
    }

    const onClickAddOneRow = () => {
        const lastAisleConfig = getLastAisleConfig(getLedDraft())
        let draftData = getLedDraft()
        const newAisleConfig = lastAisleConfig ? createNextAisleConfig(lastAisleConfig, DEFAULT_AISLE_TYPE) :
             createFirstAisleConfig(DEFAULT_AISLE_TYPE)
        draftData = [...draftData, newAisleConfig]
        setDraftData({
            data: draftData,
            stateKey: CONFIG_TYPE.LED_CONTROLLER_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
    }

    const onClickAddMultipleRows = () => {
        setOpenAddRowsModal(true)
    }

    const updateData = (stateValues) => {
        dispatch({type: 'UPDATE_ASSET_DETAIL', data: {dispatcher: dispatch, mac: selectedMac,
                aisleDetails: stateValues,
                showCustomAlert: showCustomAlert}});
    }

    const showOnlyAlert = (setAlertToShow) => {
        [setShowMissingAlert, setShowAisleDuplicateAlert, setShowPortDuplicateAlert, setShowPortLimitAlert]
            .forEach(setAlert => setAlert(setAlert === setAlertToShow))
    }

    const onClickUpdateLEDController = () => {
        if (hasLedPortAboveLimit(Object.values(getLedDraft()).map(item => item.portNumber), maxPorts)) {
            showOnlyAlert(setShowPortLimitAlert)
        } else if (!validateUpdateLedController(getLedDraft(), selectedMac, maxPorts)) {
            showOnlyAlert(setShowMissingAlert)
        } else if (!validateUniqueEntries(Object.values(getLedDraft()).map(item => item.aisleName))) {
            showOnlyAlert(setShowAisleDuplicateAlert)
        } else if (!validateUniqueEntries(Object.values(getLedDraft()).map(item => item.portNumber))) {
            showOnlyAlert(setShowPortDuplicateAlert)
        } else {
            showWarning(intl.formatMessage({id: "ledController.editWarning"}), () => {
                showOnlyAlert(null)
                updateData(getLedDraft());
            });
        }
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...headerProps} isAuthorized={isAuthorized}/>
                <Column>
                    <PaginatedTable
                        data={tableData}
                        renderRow={renderTableRow}
                        renderHeader={renderTableHeader}
                    />
                    {showMissingAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.missingfields"
                                          defaultMessage="Please enter all fields in correct format!"/>
                    </Alert>}
                    {showAisleDuplicateAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.aisleduplicatefields"
                                          defaultMessage="Please enter unique Aisle Name!"/>
                    </Alert>}
                    {showPortDuplicateAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.portduplicatefields"
                                          defaultMessage="Please enter unique Port Number!"/>
                    </Alert>}
                    {showPortLimitAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.portoutofrange"
                                          defaultMessage="Port numbers must be between 1 and {maxPorts} for this LED controller type!"
                                          values={{maxPorts}}/>
                    </Alert>}
                    <Row width="100%" widths={["100", "100", "fill", "100"]}>
                        <ConditionalWrapping
                            condition={!selectedMac}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.selectmacaddress"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickAddOneRow} type="primary" size="small"
                                           disabled={!selectedMac}
                            >
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="config.addonerow"
                                                  defaultMessage="Add One Row"/>
                            </Button>}
                        />
                        <ConditionalWrapping
                            condition={!selectedMac}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.selectmacaddress"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickAddMultipleRows} type="primary" size="small"
                                           disabled={!selectedMac}
                            >
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="config.addmultiplerows"
                                                  defaultMessage="Add Multiple Rows"/>
                            </Button>}
                        />
                        <Box></Box>
                        <ConditionalWrapping
                            condition={!selectedMac}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.selectmacaddress"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickUpdateLEDController} size="small" type="primary"
                                           disabled={!selectedMac}
                            >
                                <Icon tokens={updateTokens}/>
                                <FormattedMessage id="config.updateledcontroller" defaultMessage="Update LED Controller"/>
                            </Button>}
                        />
                    </Row>
                    {openAddRowsModal && <AddMultipleAisleRowsModal open={openAddRowsModal} setOpen={setOpenAddRowsModal}
                                                                    getDraftData={getLedDraft} setDraftData={setDraftData}
                                                                    mac={selectedMac}
                                                                    aisleTypes={[...viewModel.aisleTypes, DEFAULT_AISLE_TYPE] || []}
                                                                    maxPorts={maxPorts}
                    />}
                    <div ref={tableEndRef}/>
                </Column>
            </Column>
        );
    }

};

export default injectIntl(LEDControllerConfiguration);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/LEDControllerConfiguration.js